import React from "react"
import * as taskApi from "../api"
import { useTaskMutations } from "./use-task-mutations"
import { TaskViewModel } from "../api/task"
import { useConfirmDialog } from "../../components/ConfirmDialog"
import { isCompleted, isNotStarted } from "../../utils/status"
import { useSnackbar } from "notistack"
import { useTranslation } from "react-i18next"
import { DateTimeService } from "../../services/date-time-service"

type Translations = typeof defaultTranslations

export const useUpdateTaskStatus = ({
  onSuccess,
  task,
}: { onSuccess?: (tasks: TaskViewModel[]) => void; task?: TaskViewModel } = {}) => {
  const translations = useTranslations()
  const taskActions = useTaskMutations()
  const [getTasks] = taskApi.useLazyGetTasksQuery()
  const { enqueueSnackbar } = useSnackbar()
  const [updatedTasks, setUpdatedTasks] = React.useState<TaskViewModel[]>([])

  const onConfirm = async (taskId: string, updateData: taskApi.TaskStatusUpdateData) => {
    const updateResult = await taskActions.updateStatus(taskId, { ...updateData, updateSubtasks: true })
    if (!updateResult) return
    onSuccess?.(updateResult.tasks)
    setUpdatedTasks(updateResult.tasks)
  }
  const onCancel = async (taskId: string, updateData: taskApi.TaskStatusUpdateData) => {
    const updateResult = await taskActions.updateStatus(taskId, { ...updateData, updateSubtasks: false })
    if (!updateResult) return
    onSuccess?.(updateResult.tasks)
    setUpdatedTasks(updateResult.tasks)
  }

  const { confirm: confirmChangeAllToCompleted } = useConfirmDialog({
    onConfirm,
    onCancel,
    primaryActionButtonLabel: translations.changeThisTaskAndSubtasksButtonLabel,
    secondaryActionButtonLabel: translations.changeThisTaskOnlyButtonLabel,
    text: translations.completedDialogText,
    title: translations.dialogTitle,
  })

  const { confirm: confirmChangeAllToNotStarted } = useConfirmDialog({
    onConfirm,
    onCancel,
    primaryActionButtonLabel: translations.changeThisTaskAndSubtasksButtonLabel,
    secondaryActionButtonLabel: translations.changeThisTaskOnlyButtonLabel,
    text: translations.notStartedDialogText,
    title: translations.dialogTitle,
  })

  const updateTaskStatus = async (taskId: string, updateData: taskApi.TaskStatusUpdateData) => {
    const { status, actualEndDate, actualStartDate } = updateData
    const currentStart = task?.actualStartDate ?? ""
    const currentEnd = task?.actualEndDate ?? ""
    const now = new Date()

    // Actual start date cannot be in the future
    if (actualStartDate && new Date(actualStartDate) > now) {
      enqueueSnackbar(translations.actualStartDateCannotBeInFuture, { variant: "error" })
      return
    }
    // Actual end date cannot be in the future
    if (actualEndDate && new Date(actualEndDate) > now) {
      enqueueSnackbar(translations.actualEndDateCannotBeInFuture, { variant: "error" })
      return
    }
    if (actualStartDate && currentEnd) {
      const start = !task?.enableTimeComponent
        ? DateTimeService.toLocalDateOnly(actualStartDate)
        : new Date(actualStartDate)
      const end = !task?.enableTimeComponent ? DateTimeService.toLocalDateOnly(currentEnd) : new Date(currentEnd)

      if (start > end) {
        enqueueSnackbar(translations.actualStartDateCannotBeAfterEndDate, { variant: "error" })
        return
      }
    }
    if (actualEndDate && currentStart) {
      const start = !task?.enableTimeComponent ? DateTimeService.toLocalDateOnly(currentStart) : new Date(currentStart)
      const end = !task?.enableTimeComponent ? DateTimeService.toLocalDateOnly(actualEndDate) : new Date(actualEndDate)
      if (end < start) {
        enqueueSnackbar(translations.actualEndDateCannotBeBeforeStartDate, { variant: "error" })
        return
      }
    }
    const hasChangedToCompleted = (status && isCompleted(status)) || Boolean(actualEndDate)
    const hasChangedToNotStarted = (status && isNotStarted(status)) || actualStartDate === ""
    const response = await taskActions.updateStatus(taskId, updateData)
    if (response) {
      const { tasks } = response
      setUpdatedTasks(tasks)
    }
    const shouldFetchTasks = hasChangedToCompleted || hasChangedToNotStarted
    const tasks = shouldFetchTasks ? await getTasks({ task: taskId }).unwrap() : []

    if (hasChangedToCompleted && shouldConfirmChangeAllToCompleted(taskId, tasks)) {
      return confirmChangeAllToCompleted(taskId, updateData)
    } else if (hasChangedToNotStarted && shouldConfirmChangeAllToNotStarted(taskId, tasks)) {
      return confirmChangeAllToNotStarted(taskId, updateData)
    } else {
      if (!response) return
      onSuccess?.(response.tasks)
    }
  }

  const resetUpdatedTasksAfterSync = React.useCallback(() => {
    if (updatedTasks.length > 0) {
      setUpdatedTasks([])
    }
  }, [updatedTasks])

  return {
    updateTaskStatus,
    updatedTasks,
    resetUpdatedTasksAfterSync,
  }
}

const defaultTranslations = {
  dialogText:
    "This task has subtasks whose current status is not {{status}}. Do you want to change the status of all subtasks to {{status}} ?",
  notStartedDialogText:
    "This task has subtasks whose current status is not {{status}}. Do you want to change the status of all subtasks to {{status}} ?",
  completedDialogText:
    "This task has subtasks whose current status is not {{status}}. Do you want to change the status of all subtasks to {{status}} ?",
  dialogTitle: "Change status of its subtasks too?",
  changeThisTaskAndSubtasksButtonLabel: "Yes, change this task and subtasks",
  changeThisTaskOnlyButtonLabel: "No, change this task only",
  notStarted: "Not started",
  inProgress: "In progress",
  completed: "Completed",
  rejected: "Rejected",
  actualStartDateCannotBeAfterEndDate: "The actual start date cannot be later than the actual end date.",
  actualEndDateCannotBeBeforeStartDate: "The actual end date cannot be earlier than the actual start date.",
  actualStartDateCannotBeInFuture: "The actual start date cannot be in the future",
  actualEndDateCannotBeInFuture: "The actual end date cannot be in the future",
}

const useTranslations = (): Translations => {
  const { t: tTask } = useTranslation("task")
  const { t: tProject } = useTranslation("project")
  const notStarted = tTask("taskStatusMenu.notStarted", { defaultValue: defaultTranslations.notStarted })
  const completed = tTask("taskStatusMenu.completed", { defaultValue: defaultTranslations.completed })

  return {
    dialogText: tTask("taskStatusMenu.dialogText", {
      defaultValue: defaultTranslations.dialogText,
      status: "{{status}}",
    }),
    notStartedDialogText: tTask("taskStatusMenu.dialogText", {
      defaultValue: defaultTranslations.notStartedDialogText,
      status: notStarted,
    }),
    completedDialogText: tTask("taskStatusMenu.dialogText", {
      defaultValue: defaultTranslations.completedDialogText,
      status: completed,
    }),
    dialogTitle: tTask("taskStatusMenu.dialogTitle", { defaultValue: defaultTranslations.dialogTitle }),
    changeThisTaskAndSubtasksButtonLabel: tTask("taskStatusMenu.changeThisTaskAndSubtasksButtonLabel", {
      defaultValue: defaultTranslations.changeThisTaskAndSubtasksButtonLabel,
    }),
    changeThisTaskOnlyButtonLabel: tTask("taskStatusMenu.changeThisTaskOnlyButtonLabel", {
      defaultValue: defaultTranslations.changeThisTaskOnlyButtonLabel,
    }),
    notStarted,
    inProgress: tTask("taskStatusMenu.inProgress", { defaultValue: defaultTranslations.inProgress }),
    completed,
    rejected: tTask("taskStatusMenu.rejected", { defaultValue: defaultTranslations.rejected }),
    actualEndDateCannotBeBeforeStartDate: tProject("projectDetails.actualEndCannotBeBeforeStartError", {
      defaultValue: defaultTranslations.actualEndDateCannotBeBeforeStartDate,
    }),
    actualStartDateCannotBeAfterEndDate: tProject("projectDetails.actualStartCannotBeAfterEndError", {
      defaultValue: defaultTranslations.actualStartDateCannotBeAfterEndDate,
    }),
    actualStartDateCannotBeInFuture: tProject("projectDetails.actualStartDateCannotBeInFuture", {
      defaultValue: defaultTranslations.actualStartDateCannotBeInFuture,
    }),
    actualEndDateCannotBeInFuture: tProject("projectDetails.actualEndDateCannotBeInFuture", {
      defaultValue: defaultTranslations.actualEndDateCannotBeInFuture,
    }),
  }
}

export const shouldConfirmChangeAllToCompleted = <
  Task extends { isCompleted: boolean; ancestors: string[]; archived: boolean }
>(
  taskId: string,
  tasks: Task[]
): boolean => {
  const descendants = tasks.filter((t) => t.ancestors.includes(taskId))
  const hasDescendants = descendants.length > 0
  const areAllDescendantsCompleted = descendants.every((t) => (!t.archived ? t.isCompleted : true))
  return hasDescendants && !areAllDescendantsCompleted
}

export const shouldConfirmChangeAllToNotStarted = <
  Task extends { ancestors: string[]; isNotStarted: boolean; archived: boolean }
>(
  taskId: string,
  tasks: Task[]
): boolean => {
  const descendants = tasks.filter((t) => t.ancestors.includes(taskId))
  const hasDescendants = descendants.length > 0
  const areAllDescendantsNotStarted = descendants.every((t) => (!t.archived ? t.isNotStarted : true))
  return hasDescendants && !areAllDescendantsNotStarted
}
